import { useState } from "react"
import { MoreVertical, Eye, Trash2, Calendar } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../ui/dropdown-menu"
import { Button } from "../../ui/button"
import { useNavigate } from "react-router"
import { DeleteQuoteModal } from "./delete-quote-request"
import { UpdateQuoteModal } from "./update-quote-modal"
import { format, parseISO } from "date-fns"

interface GlamPackage {
  id: string
  title: string
  price: number
  description: string
  image_path: string
  created_at: string
  updated_at: string
}

interface QuoteRequest {
  id: string
  name: string
  email_address: string
  phone_number: string
  glam_package_id: string
  glam_spot: string
  number_of_faces: string
  booking_start_date: string
  booking_end_date: string
  booking_start_date_time: string
  booking_end_date_time: string
  home_address: string | null
  landmark: string | null
  additional_info: string | null
  google_calendar_link: string | null
  booking_name: string
  created_at: string
  updated_at: string
  glam_package: GlamPackage
}

interface QuoteRequestsTableProps {
  quotes: QuoteRequest[]
  onDeleteQuote: (id: string) => void
  onUpdateQuote: (id: string, data: Partial<QuoteRequest>) => void
}

export function QuoteRequestsTable({ quotes, onDeleteQuote, onUpdateQuote }: QuoteRequestsTableProps) {
  const navigate = useNavigate()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [updateModalOpen, setUpdateModalOpen] = useState(false)
  const [selectedQuote, setSelectedQuote] = useState<QuoteRequest | null>(null)

  const handleViewQuote = (id: string) => {
    navigate(`/control/quotes/${id}`)
  }

  const handleDeleteClick = (quote: QuoteRequest) => {
    setSelectedQuote(quote)
    setDeleteModalOpen(true)
  }

  const handleUpdateClick = (quote: QuoteRequest) => {
    setSelectedQuote(quote)
    setUpdateModalOpen(true)
  }

  const confirmDelete = () => {
    if (selectedQuote) {
      onDeleteQuote(selectedQuote.id)
      setDeleteModalOpen(false)
    }
  }

  const handleUpdateQuote = (data: Partial<QuoteRequest>) => {
    if (selectedQuote) {
      onUpdateQuote(selectedQuote.id, data)
      setUpdateModalOpen(false)
    }
  }

  const formatDateTime = (dateTimeStr: string) => {
    try {
      return format(parseISO(dateTimeStr.replace(" ", "T")), "MMM d, yyyy h:mm a")
    } catch (error) {
      return dateTimeStr
    }
  }

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy")
    } catch (error) {
      return dateStr
    }
  }

  const formatTime = (dateTimeStr: string) => {
    try {
      return format(parseISO(dateTimeStr.replace(" ", "T")), "h:mm a")
    } catch (error) {
      return dateTimeStr
    }
  }

  const getGlamSpotLabel = (glamSpot: string) => {
    return glamSpot === "studio_session" ? "Studio" : "Home Service"
  }

  if (quotes.length === 0) {
    return (
      <div className="w-full rounded-lg border bg-white p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <Calendar className="h-6 w-6 text-gray-500" />
        </div>
        <h3 className="mb-2 text-lg font-medium">No quote requests yet</h3>
        <p className="text-gray-500">When customers submit quote requests, they will appear here.</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="rounded-lg border bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-rose-50 text-left">
            <tr>
              <th className="px-4 py-3 text-sm font-medium text-gray-700">Customer Name</th>
              <th className="px-4 py-3 text-sm font-medium text-gray-700">Customer Email</th>
              <th className="px-4 py-3 text-sm font-medium text-gray-700">Glam Date</th>
              <th className="px-4 py-3 text-sm font-medium text-gray-700">Glam Time</th>
              <th className="px-4 py-3 text-sm font-medium text-gray-700">Glam Package</th>
              <th className="px-4 py-3 text-sm font-medium text-gray-700">Glam Spot</th>
              <th className="px-4 py-3 text-sm font-medium text-gray-700"></th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((quote) => (
              <tr 
                key={quote.id} 
                className="border-t hover:bg-gray-50 cursor-pointer"
                onClick={() => handleViewQuote(quote.id)}
              >
                <td className="px-4 py-3 text-sm">{quote.name}</td>
                <td className="px-4 py-3 text-sm">{quote.email_address}</td>
                <td className="px-4 py-3 text-sm">{formatDate(quote.booking_start_date)}</td>
                <td className="px-4 py-3 text-sm">{formatTime(quote.booking_start_date_time)}</td>
                <td className="px-4 py-3 text-sm">{quote.glam_package?.title || "Unknown Package"}</td>
                <td className="px-4 py-3 text-sm">{getGlamSpotLabel(quote.glam_spot)}</td>
                <td 
                  className="px-4 py-3 text-sm"
                  onClick={(e) => e.stopPropagation()} // Prevent row click when clicking menu
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => handleViewQuote(quote.id)} className="cursor-pointer">
                        <Eye className="mr-2 h-4 w-4" />
                        <span>View Quote Request</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDeleteClick(quote)} className="cursor-pointer">
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Delete Quote Request</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedQuote && (
        <>
          <DeleteQuoteModal
            isOpen={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            quoteName={selectedQuote.name}
          />
          <UpdateQuoteModal
            isOpen={updateModalOpen}
            onClose={() => setUpdateModalOpen(false)}
            onUpdate={handleUpdateQuote}
            quote={selectedQuote}
          />
        </>
      )}
    </div>
  )
}
